/* UTILS */
import router from "@/router"
import { setup, createActor, fromPromise } from "xstate"
import { globalActor } from "@/machines/globalMachine"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { COOKIES } from "@/config/constants"
import { type GlobalMachineEvent } from "@/machines/types"

/* HELPERS */
import { useSessionStorage } from "@/helpers/useSessionStorage"
import { useGotoParam } from "@/composables/useGoto"
import { usePendingUser } from "@/composables/usePendingUser"

/* STORES */
import { useUserStore } from "@/stores/user"
import { useScreeningWritingMessagesStore } from "@/features/screening/writing/store/messages.store"
import { storeToRefs } from "pinia"
import type { NextAssessmentData } from "@/types/next-assessment"
import { useAnswersStore } from "@/features/screening/shared/store/answers.store"
import { getScreeningWritingConversation } from "@/api/viktor/assessment/screening-writing"

const { removeGotoParam } = useGotoParam()

type LoadScreeningWritingResult =
  | { type: "consent_needed"; data?: NextAssessmentData }
  | { type: "redirected"; data?: NextAssessmentData }
  | { type: "assessment"; data?: NextAssessmentData }

const deleteSessionItem = () => {
  useSessionStorage().deleteSessionItem("origin")
  removeGotoParam()
}

const loadScreeningWritingData = fromPromise(
  async (): Promise<LoadScreeningWritingResult> => {
    const userStore = useUserStore()
    const { isConsentNeeded } = usePendingUser()
    const { fetchScreeningData } = useAnswersStore()
    const { nextAssessment } = storeToRefs(userStore)

    if (isConsentNeeded.value) {
      return { type: "consent_needed" }
    }

    if (!nextAssessment.value) {
      await fetchScreeningData()
    }

    const assessmentData = nextAssessment.value?.next_assessment

    if (!nextAssessment.value || !assessmentData?.assessment) {
      deleteSessionItem()
      throw new Error("NO_NEXT_ASSESSMENT")
    }

    if (!assessmentData?.assessment.includes("screening")) {
      deleteSessionItem()

      return { type: "redirected", data: assessmentData }
    }

    return { type: "assessment", data: assessmentData }
  }
)

const loadConversationState = fromPromise(
  async (): Promise<{ hasResumableChat: boolean }> => {
    const { user } = useUserStore()
    const pmUuid = useCookies().get(COOKIES.PM_UUID)
    const userUuid = user?.user?.uuid

    const conversation = await getScreeningWritingConversation(pmUuid, userUuid)

    const hasUserMessage = !!conversation?.messages?.some(
      (m) => m.role === "user"
    )

    return { hasResumableChat: hasUserMessage }
  }
)

export const screeningWritingMachine = setup({
  actors: {
    loadScreeningWritingData,
    loadConversationState,
  },
  types: {} as {
    events: any
    context: {
      errorSource?: string
    }
  },
  actions: {
    setErrorSource: ({ context }, params: { source: string }) => {
      context.errorSource = params.source
    },
  },
  guards: {
    checkNoPmUuid: () => {
      const pmUuid = useCookies().get(COOKIES.PM_UUID)
      return !pmUuid
    },
  },
}).createMachine({
  id: "screeningWritingMachine",
  initial: "checkEntry",
  states: {
    checkEntry: {
      invoke: {
        src: "loadScreeningWritingData",
        onDone: [
          {
            guard: ({ event }) => event.output?.type === "consent_needed",
            target: "intro",
          },
          {
            guard: ({ event }) => event.output?.type === "redirected",
            target: "final",
          },
          {
            target: "checkConditions",
          },
        ],
        onError: {
          actions: () => {
            globalActor.send({ type: "GLOBAL.SCREENING_WRITING.DONE" })
          },
        },
      },
    },
    checkConditions: {
      always: [
        { target: "noPmUuid", guard: "checkNoPmUuid" },
        { target: "resolveChatEntry" },
      ],
    },
    resolveChatEntry: {
      invoke: {
        src: "loadConversationState",
        // Resume the chat only when a conversation exists AND the user has
        // already answered at least once; otherwise start at the welcome step.
        onDone: [
          {
            guard: ({ event }) => !!event.output?.hasResumableChat,
            target: "chat",
          },
          { target: "intro" },
        ],
        onError: { target: "screeningError" },
      },
    },
    noPmUuid: {
      entry: [
        () => {
          globalActor.send({ type: "GLOBAL.SCREENING_WRITING.DONE" })
        },
      ],
    },
    intro: {
      entry: [
        removeGotoParam,
        () => router.replace({ name: "screeningWritingIntro" }),
      ],
      on: {
        introDone: { target: "welcome" },
      },
    },
    welcome: {
      entry: [
        () => deleteSessionItem(),
        () => router.replace({ name: "screeningWritingWelcome" }),
      ],
      on: {
        done: {
          target: "chat",
          // Do not to show the reconnect greeting if we came from welcome page
          actions: () => {
            useScreeningWritingMessagesStore().cameFromWelcomePage = true
          },
        },
        error: { target: "screeningError" },
      },
    },
    chat: {
      entry: [() => router.replace({ name: "screeningWritingChat" })],
      on: {
        done: { target: "review" },
        loadResults: { target: "loadResults" },
        error: { target: "screeningError" },
      },
    },
    review: {
      entry: [() => router.replace({ name: "screeningWritingReview" })],
      on: {
        done: { target: "loadResults" },
        error: { target: "screeningError" },
      },
    },
    loadResults: {
      entry: [() => router.replace({ name: "screeningWritingLoadResults" })],
      on: {
        done: { target: "final" },
        error: {
          target: "screeningError",
          actions: {
            type: "setErrorSource",
            params: { source: "results" },
          },
        },
      },
    },
    screeningError: {
      entry: [
        ({ context }) =>
          router.replace({
            name: "screeningWritingError",
            query: context.errorSource
              ? { source: context.errorSource }
              : undefined,
          }),
      ],
    },
    final: {
      type: "final",
    },
  },
})

export let screeningWritingActor = createActor(screeningWritingMachine)

const screeningWritingActorObserver = {
  async complete() {
    deleteSessionItem()

    const userStore = useUserStore()
    const cookies = useCookies()
    const { fetchScreeningData } = useAnswersStore()

    const { isGuestAcc, isResellerAcc, nextAssessmentName, user } =
      storeToRefs(userStore)
    const { logoutAndRedirectToAuth } = usePendingUser()
    const { clearScreeningStorage, clearScreeningCookies } = useAnswersStore()
    const messagesStore = useScreeningWritingMessagesStore()

    await fetchScreeningData()
    messagesStore.reset()

    const userEmail = user.value.user.email

    // If reseller account, redirect to thanks ATS page
    if (isResellerAcc.value) {
      globalActor.send({ type: "GLOBAL.SCREENING.RESELLER.DONE" })
      return
    }

    // If no next assessment, redirect to screening done
    if (!nextAssessmentName.value) {
      const cookiesToClear = [COOKIES.PM_UUID, COOKIES.COMPANY_UUID]
      cookiesToClear.forEach((cookie) =>
        cookies.remove(cookie, COOKIES.OPTIONS)
      )

      globalActor.send({ type: "GLOBAL.SCREENING_WRITING.DONE" })
      return
    }

    // If next assessment is meeting, redirect to meeting
    if (nextAssessmentName.value === "meeting") {
      // Pending guest user can book meeting
      globalActor.send({
        type: `GLOBAL.REDIRECT.MEETING` as GlobalMachineEvent,
      })
      return
    }

    // If guest account, redirect to register
    if (isGuestAcc.value) {
      // Guest user cannot pass other assessment make him register
      clearScreeningStorage()
      clearScreeningCookies()
      logoutAndRedirectToAuth(userEmail)
    } else {
      await userStore.fetchAssessmentSummary()
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    }
  },
}

screeningWritingActor.subscribe(screeningWritingActorObserver)

/**
 * Observateurs extérieurs, réabonnés à chaque acteur neuf.
 *
 * Sans ça, un abonné qui garde la référence de l'acteur — le store d'aide, qui
 * en tire le nom de l'assessment courant, donc les CTA du header — resterait
 * branché sur l'acteur remplacé et deviendrait sourd.
 */
const screeningWritingActorObservers: Array<(_snapshot: unknown) => void> = []

export function subscribeToScreeningWritingActor(
  observer: (_snapshot: unknown) => void
) {
  screeningWritingActorObservers.push(observer)
  screeningWritingActor.subscribe(observer)
}

let hasScreeningWritingActorStarted = false

/**
 * XState ignore `start()` sur un acteur déjà démarré (ou arrivé à son état
 * final) : aucune action d'entrée ne rejoue, la machine ne réapplique donc
 * jamais sa route et l'utilisateur reste sur la route parente nue, écran
 * blanc. Le cas se produit dès qu'on entre deux fois dans l'assessment au
 * cours de la même page — sortie par « non passé », ou renvoi dessus par le
 * workflow. On repart donc d'un acteur neuf.
 *
 * Mais UNIQUEMENT en entrant vraiment dans l'assessment, c'est-à-dire sur la
 * route parente nue, celle par laquelle la machine globale y fait entrer. Le
 * composant racine est aussi remonté sans qu'on change d'assessment — App.vue
 * reconstruit tout `main` quand la langue change — et repartir de zéro là
 * relancerait le test depuis son écran de chargement.
 */
export function startScreeningWritingActor() {
  const isEnteringAssessment =
    router.currentRoute.value.name === "screeningWriting"

  if (hasScreeningWritingActorStarted && isEnteringAssessment) {
    screeningWritingActor.stop()
    screeningWritingActor = createActor(screeningWritingMachine)
    screeningWritingActor.subscribe(screeningWritingActorObserver)
    for (const observer of screeningWritingActorObservers)
      screeningWritingActor.subscribe(observer)
  }

  hasScreeningWritingActorStarted = true
  screeningWritingActor.start()
}
